/* eslint-disable react/no-unstable-nested-components */
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import {
    Button,
    Checkbox,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownTrigger,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    DropdownItem,
    Table,
    Badge,
    MoreButton,
    AlertDialog,
    AlertDialogContent,
    Card,
    FilterButton,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { format } from "date-fns";
import { ColumnDef, Row } from "@tanstack/react-table";

import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useFetchTasks } from "../../hooks/tasks.queries";
import {
    ITask,
    TaskCategory,
    TaskListSortAttribute,
    TaskStatus,
} from "../../types/tasks.types";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    hasTimePassed,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useDebounce } from "../../../../hooks/helpers";

import ListState from "../../../../components/ListState/ListState";
import { UserPermisions } from "../../../../utils/types/user";
import {
    getStatusTag,
    getTaskUsersToDisplay,
    userIsTasKViewer,
} from "../../helpers/tasks.helper";
import { useFetchUsersList } from "../../../../hooks/queries/user-management";
import { useDeleteTasks, useUpdateTask } from "../../hooks/tasks.mutations";
import useToast from "../../../../hooks/useToast";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { UserRole } from "../../../user-management/types/user-management.types";
import { useAppSelector } from "../../../../hooks/useRedux";
import Loader from "../../../../shared-ui/Loader/Loader";
import TaskDetails from "../TaskDetails/TaskDetails";
import AddTask from "../AddTask/AddTask";
import ReassignTask from "../ReassignTask/ReassignTask";

type ComboOption = {
    value: string;
    label: string;
};

type Dialogs = "add-task" | "reassign-task" | "delete-task";

export default function MyTasksList() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [currentLimit, setCurrentLimit] = useState(LISTS_DEFAULT_LIMIT);

    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const [taskRowSelection, setTaskRowSelection] = useState({});
    const [selectedInternalUser, setSelectedInternalUser] =
        useState<ComboOption | null>(null);
    const [selectedAssignee, setSelectedAssignee] =
        useState<ComboOption | null>(null);
    const [assigneeSearchString, setAssigneeSearchString] = useState("");
    const [showCreatorListPopover, setShowCreatorListPopover] = useState(false);
    const [showAssigneeListPopover, setShowAssigneeListPopover] =
        useState(false);
    const [internalUserSearchString, setInternalUserSearchString] =
        useState("");
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>();
    const [getSelectedData, setGetSelectedData] = useState<Row<ITask>[]>();

    // params
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const pageFilter = Number(searchParams.get("page") || 1);
    const taskStatusFilter = searchParams.getAll("status[]");
    const taskCategoryFilter = searchParams.getAll("category[]");
    const clientFilter = searchParams.get("search_string") || "";

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const sortableColumns = [
        "due_date",
        "client_name",
        "created_by",
        "assigned_to",
        "task_subject",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const loggedInUser = useFetchUserDetails();

    const { practice } = useAppSelector((state) => state.userPractice);

    const update = useUpdateTask();
    const queryClient = useQueryClient();
    const deleteTasks = useDeleteTasks();
    const { toast } = useToast();

    const debouncedClientFilter = useDebounce(clientFilter);

    const handleCreatorFilter = () => {
        if (selectedInternalUser) {
            return selectedInternalUser.value;
        }

        if (
            !practice?.roles.includes(UserRole.SUPER_ADMIN) &&
            !selectedAssignee
        ) {
            return loggedInUser?.data?.user_id;
        }

        return "";
    };

    const handleAssigneeFilter = () => {
        if (selectedAssignee) {
            return selectedAssignee.value;
        }

        if (
            !practice?.roles.includes(UserRole.SUPER_ADMIN) &&
            !selectedInternalUser
        ) {
            return loggedInUser?.data?.user_id;
        }

        return "";
    };

    const handleIdQueryLogic = () => {
        if (!practice?.roles.includes(UserRole.SUPER_ADMIN)) {
            return "OR";
        }
        return "AND";
    };

    const handleViewerFilter = () => {
        if (
            !practice?.roles.includes(UserRole.SUPER_ADMIN) &&
            !selectedInternalUser &&
            !selectedAssignee
        ) {
            return loggedInUser?.data?.user_id
                ? [loggedInUser.data.user_id as string]
                : undefined;
        }

        return undefined;
    };
    const { data, isLoading, isSuccess, error } = useFetchTasks({
        page: pageFilter,
        limit: currentLimit,
        status: taskStatusFilter,
        creator_user_id: handleCreatorFilter(),
        assignee_user_id: handleAssigneeFilter(),
        viewer_user_ids: handleViewerFilter(),
        category: taskCategoryFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        search_string: debouncedClientFilter,
        sort_attribute:
            (searchParams.get("sort_attr") as TaskListSortAttribute) ||
            handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
        user_id_query_logic: handleIdQueryLogic(),
    });

    const debouncedInternalUserSearchValue = useDebounce(
        internalUserSearchString || assigneeSearchString,
        500
    );

    const isAddTaskMutating = useIsMutating(["add-todo"]);
    const isUpdateTaskMutating = useIsMutating(["update-todo"]);

    const taskToEdit = data?.data.find(
        (task) => task.todo_id === selectedTaskId
    );

    const internalUsersList = useFetchUsersList({
        page: 1,
        limit: 20,
        search_string: debouncedInternalUserSearchValue,
    });

    const handleTaskStatusFilter = (status: string) => {
        if (taskStatusFilter.includes(status)) {
            searchParams.delete("status[]", status);
        } else {
            searchParams.append("status[]", status);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleTaskCategoryFilter = (category: string) => {
        if (taskCategoryFilter.includes(category)) {
            searchParams.delete("category[]", category);
        } else {
            searchParams.append("category[]", category);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const canDeleteTask = (id: string) => {
        if (practice?.permissions?.includes(UserPermisions.TODO_ALL_DELETE)) {
            return true;
        }
        if (loggedInUser.data?.user_id === id) {
            return true;
        }

        return false;
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    // onChange handler for search input
    const handleSetClientSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);

        setSearchParams(searchParams);
    };

    const completeTask = (id: string) => {
        const selectedTaskDetails = data?.data.find((task) => {
            return task.todo_id === id;
        });

        const payload = {
            todos_to_update: [
                {
                    todo_id: selectedTaskDetails?.todo_id as string,
                    status: TaskStatus.COMPLETED,
                    title: selectedTaskDetails?.title as string,
                    description: selectedTaskDetails?.description as string,
                },
            ],
        };

        update.mutate(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });
                toast({
                    mode: "success",
                    message: "Task completed successfully!",
                });
                setDialogInView(null);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not mark task as complete",
                });
            },
        });
    };

    const selectedTasks = getSelectedData?.map(
        (item: Row<ITask>) => item.original.todo_id
    );

    const tasksDetails =
        data?.data &&
        selectedTasks &&
        (selectedTasks.map((taskId: string) =>
            data.data.find((task) => task.todo_id === taskId)
        ) as ITask[]);

    const handleDeleteTasks = () => {
        const getPayload = () => {
            if (tasksDetails && selectedTasks && selectedTasks?.length > 0) {
                return tasksDetails.map((task: ITask) => task.todo_id);
            }

            return [selectedTaskId as string];
        };

        deleteTasks.mutate(
            getPayload(),

            {
                onSuccess() {
                    queryClient.invalidateQueries({
                        queryKey: [`todos`],
                    });
                    toast({
                        mode: "success",
                        message: "Tasks deleted successfully",
                    });

                    setTaskRowSelection({});
                    setSelectedTaskId(null);
                    setDialogInView(null);
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete tasks, please try again",
                    });
                },
            }
        );
    };

    const columns: ColumnDef<ITask>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                    disabled={data?.data.some(
                        (task) =>
                            task.status === TaskStatus.COMPLETED ||
                            userIsTasKViewer(task, loggedInUser?.data?.user_id)
                    )}
                />
            ),
            cell: ({ row }) => {
                return row.original.status !== TaskStatus.COMPLETED &&
                    !userIsTasKViewer(
                        row.original,
                        loggedInUser?.data?.user_id
                    ) ? (
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                ) : null;
            },
        },
        {
            accessorKey: "due_date",
            header: "DUE DATE",
            cell: ({ row }) => (
                <span className="flex gap-2 items-center">
                    {formatDate(row.original.due_date)}

                    {hasTimePassed(row.original.due_date) &&
                        row.original.status !== TaskStatus.COMPLETED && (
                            <Badge content="Overdue" type="danger" />
                        )}
                </span>
            ),
        },
        {
            accessorKey: "client_name",
            header: "CLIENT NAME",
            cell: ({ row }) =>
                row.original.client?.client_id
                    ? displayNameInRightFormat({
                          firstName: row.original.client?.first_name,
                          lastName: row.original.client?.last_name,
                      })
                    : "--",
        },
        {
            accessorKey: "created_by",
            header: "CREATED BY",
            cell: ({ row }) =>
                row.original.creator.user_id
                    ? displayNameInRightFormat({
                          firstName: row.original.creator.first_name,
                          lastName: row.original.creator.last_name,
                      })
                    : "Mantle",
        },
        {
            accessorKey: "assigned_to",
            header: "Assignees",
            cell: ({ row }) => {
                const usersToDisplay = getTaskUsersToDisplay(
                    loggedInUser?.data?.user_id as string,
                    row.original.assignees
                );
                return usersToDisplay;
            },
        },
        {
            accessorKey: "viewers",
            header: "Viewers",
            cell: ({ row }) => {
                const usersToDisplay = getTaskUsersToDisplay(
                    loggedInUser?.data?.user_id as string,
                    row.original.viewers
                );
                return usersToDisplay;
            },
        },
        {
            accessorKey: "task_subject",
            header: "TASK SUBJECT",
            cell: ({ row }) => (
                <span className="capitalize">{row.original.title}</span>
            ),
        },
        {
            accessorKey: "status",
            header: "STATUS",
            cell: ({ row }) => getStatusTag(row.original.status),
        },
        {
            accessorKey: "task_id",
            header: "",
            cell: ({ row }) => {
                return row.original.status !== TaskStatus.COMPLETED &&
                    !userIsTasKViewer(
                        row.original,
                        loggedInUser?.data?.user_id
                    ) ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>

                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    completeTask(row.original.todo_id);
                                }}
                            >
                                Mark as Completed
                            </DropdownItem>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();

                                    setDialogInView("reassign-task");
                                    setSelectedTaskId(row.original.todo_id);
                                }}
                            >
                                Re-Assign
                            </DropdownItem>
                            {canDeleteTask(row.original.creator.user_id) && (
                                <DropdownItem
                                    className="text-danger"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDialogInView("delete-task");
                                        setSelectedTaskId(row.original.todo_id);
                                    }}
                                >
                                    Delete Task
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                ) : null;
            },
        },
    ];

    return (
        <>
            <Dialog open={Boolean(searchParams.get("task_id"))}>
                <DialogContent
                    title="Task Details"
                    handleCloseDialog={() => {
                        searchParams.delete("task_id");
                        setSearchParams(searchParams, { replace: true });
                    }}
                    submitBtnFormValue="edit-task"
                    isSubmitBtnDisabled={isUpdateTaskMutating > 0}
                    isCancelBtnDisabled={isUpdateTaskMutating > 0}
                    showFooter={false}
                >
                    <TaskDetails
                        setHideModal={() => {
                            searchParams.delete("task_id");
                            setSearchParams(searchParams, {
                                replace: true,
                            });
                        }}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "add-task"}>
                <DialogContent
                    title="Add task"
                    saveText="Add task"
                    variant="center"
                    handleCloseDialog={() => setDialogInView(null)}
                    submitBtnFormValue="add-todo"
                    isSubmitBtnDisabled={isAddTaskMutating > 0}
                    isCancelBtnDisabled={isAddTaskMutating > 0}
                >
                    <AddTask onFormSubmit={() => setDialogInView(null)} />
                </DialogContent>
            </Dialog>
            {taskToEdit ? (
                <Dialog open={dialogInView === "reassign-task"}>
                    <DialogContent
                        title="Re-assign task"
                        saveText="Re-assign task"
                        variant="center"
                        handleCloseDialog={() => {
                            setDialogInView(null);
                            setSelectedTaskId(null);
                        }}
                        submitBtnFormValue="reassign-task"
                        isSubmitBtnDisabled={isUpdateTaskMutating > 0}
                        isCancelBtnDisabled={isUpdateTaskMutating > 0}
                    >
                        <ReassignTask
                            onFormSubmit={() => {
                                setDialogInView(null);
                                setSelectedTaskId(null);
                            }}
                            task={taskToEdit}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <AlertDialog open={dialogInView === "delete-task"}>
                <AlertDialogContent
                    title="Are you sure you want to delete this task?"
                    description="Are you sure you want to delete this task? Please
          note that this action cannot be undone."
                    onCancelClick={() => setDialogInView(null)}
                    saveText="Yes, delete task"
                    cancelText="Cancel"
                    isSaveBtnDisabled={deleteTasks.isLoading}
                    isDeleting
                    onSaveClick={() => handleDeleteTasks()}
                />
            </AlertDialog>

            <Card classNames="mb-[--spacing-items]">
                <div className="flex flex-col gap-y-[--spacing-items]">
                    <SearchInput
                        placeholder="Search by client name or chart number"
                        containerClass="w-[400px] max-w-full"
                        onChange={handleSetClientSearch}
                        defaultValue={clientFilter}
                    />
                    <div className="flex items-center justify-between  flex-wrap gap-[--spacing-items]">
                        <div className="flex items-center flex-wrap gap-[--spacing-items]">
                            <Popover
                                open={showCreatorListPopover}
                                onOpenChange={setShowCreatorListPopover}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() =>
                                        setShowCreatorListPopover(true)
                                    }
                                >
                                    <FilterButton
                                        className={
                                            selectedInternalUser
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {selectedInternalUser
                                            ? truncateString(
                                                  selectedInternalUser.label
                                              )
                                            : "Task creators"}
                                    </FilterButton>
                                </PopoverTrigger>

                                <PopoverContent align="start">
                                    <SearchInput
                                        placeholder="Search by creator name"
                                        onChange={(e) =>
                                            setInternalUserSearchString(
                                                e.target.value
                                            )
                                        }
                                    />

                                    <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                        {internalUsersList.isLoading && (
                                            <Loader wrapperClassName="h-[150px]" />
                                        )}

                                        {internalUsersList.error && (
                                            <ListState
                                                isError
                                                context="general"
                                                stateHelperText="Please try again later"
                                                errorMsg={
                                                    internalUsersList.error
                                                        ?.response?.data
                                                        ?.message ||
                                                    "Error loading creators"
                                                }
                                                cardClass="h-[150px]"
                                            />
                                        )}

                                        {internalUsersList.data?.data &&
                                            internalUsersList.isSuccess && (
                                                <>
                                                    <Button
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="font-medium capitalize w-full justify-start shrink-0"
                                                        onClick={() => {
                                                            setSelectedInternalUser(
                                                                null
                                                            );
                                                            setInternalUserSearchString(
                                                                ""
                                                            );
                                                            setShowCreatorListPopover(
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        All
                                                    </Button>
                                                    {internalUsersList?.data?.data.map(
                                                        (creator) => (
                                                            <Button
                                                                key={
                                                                    creator.user_id
                                                                }
                                                                size="auto"
                                                                variant="can-hover"
                                                                className="font-medium capitalize w-full justify-start shrink-0"
                                                                onClick={() => {
                                                                    setSelectedInternalUser(
                                                                        {
                                                                            label: removeEnumUnderscore(
                                                                                `${creator.last_name} ${creator.first_name}`
                                                                            ),
                                                                            value: creator.user_id,
                                                                        }
                                                                    );

                                                                    setShowCreatorListPopover(
                                                                        false
                                                                    );

                                                                    setInternalUserSearchString(
                                                                        ""
                                                                    );
                                                                }}
                                                            >
                                                                {displayNameInRightFormat(
                                                                    {
                                                                        firstName:
                                                                            creator.first_name,
                                                                        lastName:
                                                                            creator.last_name,
                                                                    }
                                                                )}
                                                            </Button>
                                                        )
                                                    )}
                                                </>
                                            )}
                                    </div>
                                </PopoverContent>
                            </Popover>

                            <Popover
                                open={showAssigneeListPopover}
                                onOpenChange={setShowAssigneeListPopover}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() =>
                                        setShowAssigneeListPopover(true)
                                    }
                                >
                                    <FilterButton
                                        className={
                                            selectedAssignee
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {" "}
                                        {selectedAssignee
                                            ? truncateString(
                                                  selectedAssignee.label
                                              )
                                            : "Task assignees"}
                                    </FilterButton>
                                </PopoverTrigger>

                                <PopoverContent align="start">
                                    <SearchInput
                                        placeholder="Search by assignee name"
                                        onChange={(e) =>
                                            setAssigneeSearchString(
                                                e.target.value
                                            )
                                        }
                                    />

                                    <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                        {internalUsersList.isLoading && (
                                            <Loader wrapperClassName="h-[150px]" />
                                        )}

                                        {internalUsersList.error && (
                                            <ListState
                                                isError
                                                context="general"
                                                stateHelperText="Please try again later"
                                                errorMsg={
                                                    error?.response?.data
                                                        ?.message ||
                                                    "Error loading assignees"
                                                }
                                                cardClass="h-[150px]"
                                            />
                                        )}

                                        {internalUsersList?.data && isSuccess && (
                                            <>
                                                <Button
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start shrink-0 text-[13px]"
                                                    onClick={() => {
                                                        setSelectedAssignee(
                                                            null
                                                        );
                                                        setAssigneeSearchString(
                                                            ""
                                                        );
                                                        setShowAssigneeListPopover(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    All
                                                </Button>
                                                {internalUsersList?.data?.data.map(
                                                    (assignee) => (
                                                        <Button
                                                            key={
                                                                assignee?.user_id
                                                            }
                                                            size="auto"
                                                            variant="can-hover"
                                                            className="font-medium capitalize w-full justify-start shrink-0 text-[13px]"
                                                            onClick={() => {
                                                                setSelectedAssignee(
                                                                    {
                                                                        label: removeEnumUnderscore(
                                                                            `${assignee.last_name} ${assignee.first_name}`
                                                                        ),
                                                                        value: assignee.user_id,
                                                                    }
                                                                );

                                                                setShowAssigneeListPopover(
                                                                    false
                                                                );
                                                                setAssigneeSearchString(
                                                                    ""
                                                                );
                                                            }}
                                                        >
                                                            {displayNameInRightFormat(
                                                                {
                                                                    firstName:
                                                                        assignee.first_name,
                                                                    lastName:
                                                                        assignee.last_name,
                                                                }
                                                            )}
                                                        </Button>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Status</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent
                                    align="start"
                                    width="auto"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                >
                                    {Object.values(TaskStatus).map((status) => (
                                        <DropdownItem
                                            key={status}
                                            onClick={() =>
                                                handleTaskStatusFilter(status)
                                            }
                                            isMulti
                                            itemValue={status}
                                            values={taskStatusFilter}
                                        >
                                            {removeEnumUnderscore(status)}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton> Task category</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent
                                    align="start"
                                    width="auto"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                >
                                    {Object.values(TaskCategory).map(
                                        (category) => (
                                            <DropdownItem
                                                key={category}
                                                onClick={() =>
                                                    handleTaskCategoryFilter(
                                                        category
                                                    )
                                                }
                                                isMulti
                                                itemValue={category}
                                                values={taskCategoryFilter}
                                            >
                                                {removeEnumUnderscore(category)}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="end"
                                    className="flex gap-y-2"
                                >
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={fromDateFilterToDate}
                                        onChange={handleFromDateFilterChange}
                                        maxDate={
                                            toDateFilterToDate ||
                                            new Date(Date.now())
                                        }
                                        isClearable
                                    />

                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <Button onClick={() => setDialogInView("add-task")}>
                            Create new task
                        </Button>
                    </div>
                </div>

                <TableFiltersDisplay
                    appliedFilters={[
                        { key: "category[]", values: taskCategoryFilter },
                        { key: "status[]", values: taskStatusFilter },
                    ]}
                    wrapperClass="mt-[--spacing-items]"
                />
            </Card>

            {isLoading && <Loader />}

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display tasks at this time please try again later`
                    }
                />
            )}

            {data && isSuccess && (
                <>
                    {data.data.length === 0 && (
                        <ListState
                            stateHelperText="Tasks will appear here once they are added"
                            emptyMessage="No tasks added yet"
                            isError={false}
                            emptyBtnProps={{
                                showButton: true,
                                onButtonClick: () =>
                                    setDialogInView("add-task"),
                                buttonText: "Create New Task",
                            }}
                        />
                    )}

                    {data.data.length > 0 && (
                        <>
                            {getTotalAmountOfTableRowsSelected(taskRowSelection)
                                .amount > 0 && (
                                <div className="flex items-center mb-[--spacing-items] gap-x-[--spacing-items]">
                                    <span className="font-medium text-base">
                                        {
                                            getTotalAmountOfTableRowsSelected(
                                                taskRowSelection
                                            ).amountString
                                        }
                                    </span>
                                    <Button
                                        aria-label="actions"
                                        variant="secondary"
                                        onClick={() =>
                                            setDialogInView("delete-task")
                                        }
                                    >
                                        Delete tasks
                                    </Button>
                                </div>
                            )}

                            <Table
                                columns={columns}
                                data={data?.data}
                                handleRowClick={(row) => {
                                    searchParams.set(
                                        "task_id",
                                        row.original.todo_id
                                    );

                                    setSearchParams(searchParams);
                                }}
                                setGetSelectedData={setGetSelectedData}
                                rowSelection={taskRowSelection}
                                setRowSelection={setTaskRowSelection}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                hasPagination={data.total_count > 20}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        onPageChange={handlePageChange}
                                        limit={currentLimit}
                                        onLimitChange={(l) =>
                                            setCurrentLimit(l)
                                        }
                                        currentPage={pageFilter}
                                    />
                                }
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
}
